import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import axios from 'axios';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { 
  CheckCircleIcon, 
  ExclamationCircleIcon,
  ChevronRightIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarIcon,
  UserGroupIcon,
  CreditCardIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import BookingSummary from './components/BookingSummary';
import PaymentForm from './components/PaymentForm';
import BookingVehicleSelection from '../../components/Booking/BookingVehicleSelection';

const BookingPage = () => {
  const { tourId } = useParams();
  const navigate = useNavigate();
  const [bookingStep, setBookingStep] = useState(1);
  const [bookingData, setBookingData] = useState(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  
  // Check if user is logged in
  const isLoggedIn = !!localStorage.getItem('token');
  
  // Redirect to login if not logged in
  useEffect(() => {
    if (!isLoggedIn) {
      localStorage.setItem('redirectAfterLogin', `/booking/${tourId}`);
      navigate('/login');
    }
  }, [isLoggedIn, navigate, tourId]);
  
  // Fetch tour details
  const fetchTourDetails = async () => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    const response = await axios.get(`${apiUrl}/api/v1/tours/${tourId}`);
    console.log('Tour API response:', response.data);
    // Extract the tour data from the response
    // The API might return the tour in a nested 'data' property
    return response.data.data || response.data;
  };
  
  // Fetch user details
  const fetchUserDetails = async () => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    try {
      // Get the token from localStorage
      const token = localStorage.getItem('token');
      
      // Check if token exists
      if (!token) {
        throw new Error('Authentication token is missing');
      }
      
      console.log('Using token for user details:', token.substring(0, 10) + '...');
      
      const response = await axios.get(`${apiUrl}/api/v1/auth/user`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching user details:', error.response?.data);
      
      // If we get a 401 error, the token might be expired
      if (error.response && error.response.status === 401) {
        // Instead of redirecting immediately, we'll return fallback data
        // This allows the booking form to still function without user data
        return fallbackUser;
      }
      
      throw error;
    }
  };
  
  // Tour details query
  const { 
    data: tour, 
    isLoading: isTourLoading, 
    error: tourError 
  } = useQuery(['tour', tourId], fetchTourDetails, {
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  
  // User details query
  const { 
    data: user, 
    isLoading: isUserLoading, 
    error: userError 
  } = useQuery('user', fetchUserDetails, {
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: isLoggedIn,
    // Don't retry on 401 errors
    retry: (failureCount, error) => {
      if (error?.response?.status === 401) return false;
      return failureCount < 3;
    },
    // Use fallback data if there's an error
    onError: (error) => {
      console.log('User query error handled gracefully:', error.message);
    }
  });
  
  // State for error modal
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorDetails, setErrorDetails] = useState({
    message: '',
    details: null
  });

  // Create booking mutation
  const createBookingMutation = useMutation(
    async (bookingData) => {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      try {
        // Get the token from localStorage
        const token = localStorage.getItem('token');
        
        // Check if token exists
        if (!token) {
          throw new Error('Authentication token is missing. Please log in again.');
        }
        
        console.log('Using token for authentication:', token.substring(0, 10) + '...');
        
        const response = await axios.post(`${apiUrl}/api/v1/bookings`, bookingData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
        return response.data;
      } catch (error) {
        console.error('Booking submission error:', error.response?.data);
        
        // If we get a 401 error, the token might be expired
        if (error.response && error.response.status === 401) {
          // Clear the token and redirect to login
          localStorage.removeItem('token');
          alert('Your session has expired. Please log in again.');
          navigate('/login');
        }
        
        throw error;
      }
    },
    {
      onSuccess: (data) => {
        setBookingData(data);
        
        // Check if this was a Pay Later booking
        if (bookingFormik.values.paymentOption === 'pay_later') {
          // Skip payment step and go directly to confirmation
          setBookingStep(3);
        } else {
          // For Pay Now option, proceed to payment step
          setBookingStep(2);
        }
      },
      onError: (error) => {
        console.error('Booking mutation error:', error.response?.data);
        
        const responseData = error.response?.data;
        console.log('Response data for debugging:', responseData);
        
        // Check if this is a duplicate booking error
        if (responseData && responseData.message && responseData.message.includes('already have a booking')) {
          console.log('Duplicate booking detected, showing modal with details:', responseData.details);
          
          // Show the duplicate booking error modal with details
          setErrorDetails({
            message: responseData.message,
            details: responseData.details || {}
          });
          setShowErrorModal(true);
          return;
        }
        
        // Handle other validation errors
        const validationErrors = responseData?.errors;
        let errorMessage = responseData?.message || error.message;
        
        // Format validation errors for display
        if (validationErrors) {
          const errorDetails = Object.entries(validationErrors)
            .map(([field, errors]) => `${field}: ${errors.join(', ')}`)
            .join('\n');
          errorMessage += '\n\nDetails:\n' + errorDetails;
        }
        
        // Show error message to user
        alert('Error creating booking: ' + errorMessage);
      }
    }
  );
  
  // Process payment mutation
  const processPaymentMutation = useMutation(
    async (paymentData) => {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const response = await axios.post(`${apiUrl}/payments`, {
        bookingId: bookingData.id,
        ...paymentData
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        setBookingStep(3);
      }
    }
  );
  
  // Fallback data for development/testing
  const fallbackTour = {
    id: parseInt(tourId),
    title: 'Savannah Safari Experience',
    description: 'Experience the breathtaking beauty of our most famous wildlife reserve with incredible animal sightings.',
    price: 1299,
    duration: 5,
    location: 'Savannah National Reserve',
    image: `https://source.unsplash.com/random/800x600?safari,wildlife&sig=1`,
    rating: 4.8,
    reviewCount: 124,
    category: 'Wildlife Safari',
    groupSize: 6
  };
  
  const fallbackUser = {
    id: 1,
    name: 'John Doe',
    email: 'john.doe@example.com',
    phone: '+1234567890'
  };
  
  // Use fallback data when in development or when there's an error
  const tourData = tour || fallbackTour;
  
  // Ensure the tour price is correctly extracted
  if (tourData && typeof tourData.price !== 'number') {
    // Check if price is in a different format or location in the API response
    if (tourData.data && typeof tourData.data.price === 'number') {
      tourData.price = tourData.data.price;
    } else if (typeof tourData.price === 'string') {
      // Convert string price to number
      tourData.price = parseFloat(tourData.price);
    } else {
      // Set a default price if none is found
      tourData.price = fallbackTour.price;
    }
  }
  
  const userData = user || fallbackUser;
  
  // Booking form validation schema
  const bookingValidationSchema = Yup.object({
    fullName: Yup.string()
      .required('Full name is required'),
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    phone: Yup.string()
      .required('Phone number is required'),
    date: Yup.date()
      .required('Please select a date')
      .min(new Date(), 'Date cannot be in the past'),
    travelers: Yup.number()
      .required('Number of travelers is required')
      .min(1, 'Minimum 1 traveler')
      .max(
        typeof tourData.groupSize === 'number' ? tourData.groupSize : 10,
        `Maximum ${typeof tourData.groupSize === 'number' ? tourData.groupSize : 10} travelers`
      ),
    specialRequirements: Yup.string()
      .max(500, 'Maximum 500 characters'),
    paymentOption: Yup.string()
      .required('Please select a payment option')
      .oneOf(['pay_now', 'pay_later'], 'Invalid payment option')
  });
  
  // Initialize booking form
  const bookingFormik = useFormik({
    initialValues: {
      fullName: userData.name || '',
      email: userData.email || '',
      phone: userData.phone || '',
      date: '',
      travelers: 1,
      specialRequirements: '',
      paymentOption: 'pay_now' // Default to Pay Now option
    },
    validationSchema: bookingValidationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      // Format date as YYYY-MM-DD
      const formatDate = (date) => {
        if (!date) return '';
        const d = new Date(date);
        return d.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
      };
      
      // Calculate the total price
      const price = typeof tourData.price === 'number' ? tourData.price : 0;
      const totalPrice = values.travelers * price;
      
      // Create expiration date for pay later option (7 days from now)
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + 7);
      
      // Log the data being sent for debugging
      const bookingData = {
        tour_id: parseInt(tourId), // Use the tourId from URL params directly
        booking_date: formatDate(values.date), // Use selected date as booking date
        travel_date: formatDate(values.date), // Selected date as travel date
        adults: values.travelers,
        children: 0,
        special_requirements: values.specialRequirements || '',
        contact_name: values.fullName,
        contact_email: values.email,
        contact_phone: values.phone,
        payment_method: values.paymentOption === 'pay_later' ? 'bank_transfer' : 'card', // Using bank_transfer for pay_later option
        payment_status: values.paymentOption === 'pay_later' ? 'pending' : 'awaiting',
        payment_due_date: values.paymentOption === 'pay_later' ? formatDate(expirationDate) : null,
        coupon_code: '',
        total_price: totalPrice,
        total_amount: totalPrice
      };
      
      console.log('Submitting booking data:', bookingData);
      createBookingMutation.mutate(bookingData);
    }
  });
  
  // Payment form validation schema
  const paymentValidationSchema = Yup.object({
    cardName: Yup.string()
      .required('Name on card is required'),
    cardNumber: Yup.string()
      .required('Card number is required')
      .matches(/^[0-9]{16}$/, 'Card number must be 16 digits'),
    expiryDate: Yup.string()
      .required('Expiry date is required')
      .matches(/^(0[1-9]|1[0-2])\/[0-9]{2}$/, 'Expiry date must be in MM/YY format'),
    cvv: Yup.string()
      .required('CVV is required')
      .matches(/^[0-9]{3,4}$/, 'CVV must be 3 or 4 digits'),
    billingAddress: Yup.string()
      .required('Billing address is required'),
    city: Yup.string()
      .required('City is required'),
    zipCode: Yup.string()
      .required('ZIP code is required'),
    country: Yup.string()
      .required('Country is required')
  });
  
  // Initialize payment form
  const paymentFormik = useFormik({
    initialValues: {
      cardName: '',
      cardNumber: '',
      expiryDate: '',
      cvv: '',
      billingAddress: '',
      city: '',
      zipCode: '',
      country: ''
    },
    validationSchema: paymentValidationSchema,
    onSubmit: (values) => {
      processPaymentMutation.mutate({
        paymentMethod: 'credit_card',
        amount: bookingData.totalPrice,
        currency: 'USD',
        ...values
      });
    }
  });
  
  // Handle traveler count change
  const handleTravelerChange = (change) => {
    const newValue = bookingFormik.values.travelers + change;
    const maxTravelers = typeof tourData.groupSize === 'number' ? tourData.groupSize : 10; // Default to 10 if undefined
    if (newValue >= 1 && newValue <= maxTravelers) {
      bookingFormik.setFieldValue('travelers', newValue);
    }
  };
  
  // Calculate total price
  const calculateTotal = () => {
    const travelers = bookingFormik.values.travelers || 1;
    
    // Ensure we have a valid price
    let price = 0;
    if (typeof tourData.price === 'number' && !isNaN(tourData.price)) {
      price = tourData.price;
    } else if (typeof tourData.price === 'string') {
      price = parseFloat(tourData.price) || 0;
    }
    
    console.log('Tour price used for calculation:', price);
    return travelers * price;
  };
  
  if (isTourLoading || (isUserLoading && isLoggedIn)) {
    return (
      <div className="container-custom py-20">
        <div className="flex justify-center items-center h-96">
          <LoadingSpinner size="large" />
        </div>
      </div>
    );
  }
  
  if (tourError || (userError && isLoggedIn)) {
    return (
      <div className="container-custom py-20">
        <ErrorMessage 
          message="Failed to load necessary data. Please try again later." 
          onRetry={() => window.location.reload()} 
        />
      </div>
    );
  }
  
  // Format date for display
  const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      {/* Duplicate Booking Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
            <button 
              onClick={() => setShowErrorModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="flex items-center mb-4 text-red-600">
              <ExclamationCircleIcon className="h-8 w-8 mr-3" />
              <h3 className="text-xl font-bold">Duplicate Booking</h3>
            </div>
            
            <p className="mb-4">{errorDetails.message}</p>
            
            {errorDetails.details && (
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h4 className="font-medium mb-2">Existing Booking Details:</h4>
                <ul className="space-y-2">
                  <li className="flex justify-between">
                    <span className="text-gray-600">Booking Number:</span>
                    <span className="font-medium">{errorDetails.details.booking_number}</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-gray-600">Travel Date:</span>
                    <span className="font-medium">{formatDisplayDate(errorDetails.details.travel_date)}</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-gray-600">Payment Status:</span>
                    <span className="font-medium capitalize">
                      {errorDetails.details.payment_status ? errorDetails.details.payment_status.replace(/_/g, ' ') : 'Pending'}
                    </span>
                  </li>
                </ul>
              </div>
            )}
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowErrorModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200"
              >
                Close
              </button>
              <Link
                to="/dashboard/bookings"
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors duration-200"
              >
                View My Bookings
              </Link>
            </div>
          </div>
        </div>
      )}
      
      <div className="container-custom">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Book Your Tour</h1>
          <p className="text-gray-600">Complete your booking in just a few steps</p>
        </div>
        
        {/* Booking Steps */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className={`flex items-center mb-4 md:mb-0 ${bookingStep >= 1 ? 'text-primary' : 'text-gray-400'}`}>
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                bookingStep >= 1 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'
              } mr-2`}>
                {bookingStep > 1 ? <CheckCircleIcon className="h-5 w-5" /> : '1'}
              </div>
              <span className="font-medium">Booking Details</span>
            </div>
            
            <ChevronRightIcon className="h-5 w-5 text-gray-300 hidden md:block" />
            
            <div className={`flex items-center mb-4 md:mb-0 ${bookingStep >= 2 ? 'text-primary' : 'text-gray-400'}`}>
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                bookingStep >= 2 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'
              } mr-2`}>
                {bookingStep > 2 ? <CheckCircleIcon className="h-5 w-5" /> : '2'}
              </div>
              <span className="font-medium">Payment</span>
            </div>
            
            <ChevronRightIcon className="h-5 w-5 text-gray-300 hidden md:block" />
            
            <div className={`flex items-center ${bookingStep >= 3 ? 'text-primary' : 'text-gray-400'}`}>
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                bookingStep >= 3 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'
              } mr-2`}>
                {bookingStep > 3 ? <CheckCircleIcon className="h-5 w-5" /> : '3'}
              </div>
              <span className="font-medium">Confirmation</span>
            </div>
          </div>
        </div>
        
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <div className="lg:w-2/3">
            {/* Step 1: Booking Details */}
            {bookingStep === 1 && (
              <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                <h2 className="text-xl font-bold mb-6">Booking Information</h2>
                
                <form onSubmit={bookingFormik.handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {/* Full Name */}
                    <div>
                      <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <UserIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          id="fullName"
                          name="fullName"
                          value={bookingFormik.values.fullName}
                          onChange={bookingFormik.handleChange}
                          onBlur={bookingFormik.handleBlur}
                          className={`block w-full pl-10 pr-3 py-2 border ${
                            bookingFormik.touched.fullName && bookingFormik.errors.fullName
                              ? 'border-red-500'
                              : 'border-gray-300'
                          } rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                        />
                      </div>
                      {bookingFormik.touched.fullName && bookingFormik.errors.fullName && (
                        <p className="mt-1 text-sm text-red-500">{bookingFormik.errors.fullName}</p>
                      )}
                    </div>
                    
                    {/* Email */}
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={bookingFormik.values.email}
                          onChange={bookingFormik.handleChange}
                          onBlur={bookingFormik.handleBlur}
                          className={`block w-full pl-10 pr-3 py-2 border ${
                            bookingFormik.touched.email && bookingFormik.errors.email
                              ? 'border-red-500'
                              : 'border-gray-300'
                          } rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                        />
                      </div>
                      {bookingFormik.touched.email && bookingFormik.errors.email && (
                        <p className="mt-1 text-sm text-red-500">{bookingFormik.errors.email}</p>
                      )}
                    </div>
                    
                    {/* Phone */}
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <PhoneIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={bookingFormik.values.phone}
                          onChange={bookingFormik.handleChange}
                          onBlur={bookingFormik.handleBlur}
                          className={`block w-full pl-10 pr-3 py-2 border ${
                            bookingFormik.touched.phone && bookingFormik.errors.phone
                              ? 'border-red-500'
                              : 'border-gray-300'
                          } rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                        />
                      </div>
                      {bookingFormik.touched.phone && bookingFormik.errors.phone && (
                        <p className="mt-1 text-sm text-red-500">{bookingFormik.errors.phone}</p>
                      )}
                    </div>
                    
                    {/* Tour Date */}
                    <div>
                      <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                        Tour Date
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <CalendarIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="date"
                          id="date"
                          name="date"
                          value={bookingFormik.values.date}
                          onChange={bookingFormik.handleChange}
                          onBlur={bookingFormik.handleBlur}
                          min={new Date().toISOString().split('T')[0]}
                          className={`block w-full pl-10 pr-3 py-2 border ${
                            bookingFormik.touched.date && bookingFormik.errors.date
                              ? 'border-red-500'
                              : 'border-gray-300'
                          } rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                        />
                      </div>
                      {bookingFormik.touched.date && bookingFormik.errors.date && (
                        <p className="mt-1 text-sm text-red-500">{bookingFormik.errors.date}</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Travelers */}
                  <div className="mb-6">
                    <label htmlFor="travelers" className="block text-sm font-medium text-gray-700 mb-1">
                      Number of Travelers
                    </label>
                    <div className="flex items-center">
                      <button
                        type="button"
                        onClick={() => handleTravelerChange(-1)}
                        disabled={bookingFormik.values.travelers <= 1}
                        className={`px-3 py-2 border border-gray-300 rounded-l-md ${
                          bookingFormik.values.travelers <= 1
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        -
                      </button>
                      <div className="relative flex-1 max-w-[150px]">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <UserGroupIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="number"
                          id="travelers"
                          name="travelers"
                          value={bookingFormik.values.travelers}
                          onChange={bookingFormik.handleChange}
                          onBlur={bookingFormik.handleBlur}
                          min="1"
                          max={typeof tourData.groupSize === 'number' ? tourData.groupSize : 10}
                          className={`block w-full pl-10 pr-3 py-2 border-t border-b ${
                            bookingFormik.touched.travelers && bookingFormik.errors.travelers
                              ? 'border-red-500'
                              : 'border-gray-300'
                          } focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-center`}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => handleTravelerChange(1)}
                        disabled={bookingFormik.values.travelers >= (typeof tourData.groupSize === 'number' ? tourData.groupSize : 10)}
                        className={`px-3 py-2 border border-gray-300 rounded-r-md ${
                          bookingFormik.values.travelers >= (typeof tourData.groupSize === 'number' ? tourData.groupSize : 10)
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        +
                      </button>
                      <span className="ml-3 text-sm text-gray-500">
                        Maximum {typeof tourData.groupSize === 'number' ? tourData.groupSize : 10} travelers per booking
                      </span>
                    </div>
                    {bookingFormik.touched.travelers && bookingFormik.errors.travelers && (
                      <p className="mt-1 text-sm text-red-500">{bookingFormik.errors.travelers}</p>
                    )}
                  </div>
                  
                  {/* Special Requirements */}
                  <div className="mb-6">
                    <label htmlFor="specialRequirements" className="block text-sm font-medium text-gray-700 mb-1">
                      Special Requirements (Optional)
                    </label>
                    <textarea
                      id="specialRequirements"
                      name="specialRequirements"
                      value={bookingFormik.values.specialRequirements}
                      onChange={bookingFormik.handleChange}
                      onBlur={bookingFormik.handleBlur}
                      rows="3"
                      className={`block w-full px-3 py-2 border ${
                        bookingFormik.touched.specialRequirements && bookingFormik.errors.specialRequirements
                          ? 'border-red-500'
                          : 'border-gray-300'
                      } rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                      placeholder="Any dietary requirements, accessibility needs, etc."
                    ></textarea>
                    {bookingFormik.touched.specialRequirements && bookingFormik.errors.specialRequirements && (
                      <p className="mt-1 text-sm text-red-500">{bookingFormik.errors.specialRequirements}</p>
                    )}
                  </div>
                  
                  <BookingVehicleSelection 
                    tourId={tourId}
                    bookingDates={bookingFormik.values.date}
                    onVehicleSelect={setSelectedVehicleId}
                    selectedVehicleId={selectedVehicleId}
                  />
                  
                  {/* Payment Options */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-3">Payment Options</h3>
                    <div className="space-y-4">
                      {/* Pay Now Option */}
                      <div className="border border-gray-200 rounded-lg p-4 hover:border-primary transition-colors duration-200">
                        <label className="flex items-start cursor-pointer">
                          <input
                            type="radio"
                            name="paymentOption"
                            value="pay_now"
                            checked={bookingFormik.values.paymentOption === 'pay_now'}
                            onChange={() => bookingFormik.setFieldValue('paymentOption', 'pay_now')}
                            className="mt-1 h-4 w-4 text-primary focus:ring-primary border-gray-300"
                          />
                          <div className="ml-3">
                            <span className="block font-medium">Pay Now</span>
                            <span className="text-sm text-gray-500">Secure your booking immediately with online payment</span>
                          </div>
                        </label>
                      </div>
                      
                      {/* Pay Later Option */}
                      <div className="border border-gray-200 rounded-lg p-4 hover:border-primary transition-colors duration-200">
                        <label className="flex items-start cursor-pointer">
                          <input
                            type="radio"
                            name="paymentOption"
                            value="pay_later"
                            checked={bookingFormik.values.paymentOption === 'pay_later'}
                            onChange={() => bookingFormik.setFieldValue('paymentOption', 'pay_later')}
                            className="mt-1 h-4 w-4 text-primary focus:ring-primary border-gray-300"
                          />
                          <div className="ml-3">
                            <span className="block font-medium">Pay Later</span>
                            <span className="text-sm text-gray-500">Reserve now and pay at our office within 7 days</span>
                            <div className="mt-2 p-2 bg-yellow-50 border border-yellow-100 rounded text-xs text-yellow-800">
                              <p><strong>Note:</strong> Your booking will be held for 7 days. If payment is not received within this period, your reservation will be automatically canceled.</p>
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={createBookingMutation.isLoading}
                      className="bg-primary hover:bg-primary-dark text-white py-2 px-6 rounded-md transition-colors duration-300"
                    >
                      {createBookingMutation.isLoading ? 'Processing...' : 
                        bookingFormik.values.paymentOption === 'pay_now' ? 'Continue to Payment' : 'Confirm Reservation'}
                    </button>
                  </div>
                </form>
              </div>
            )}
            
            {/* Step 2: Payment */}
            {bookingStep === 2 && (
              <PaymentForm 
                formik={paymentFormik} 
                isLoading={processPaymentMutation.isLoading}
                bookingData={bookingData}
              />
            )}
            
            {/* Step 3: Confirmation */}
            {bookingStep === 3 && (
              <div className="bg-white rounded-lg shadow-md p-6 mb-8 text-center">
                <div className="flex justify-center mb-4">
                  <div className="bg-green-100 rounded-full p-3">
                    <CheckCircleIcon className="h-16 w-16 text-green-500" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Booking Confirmed!</h2>
                {bookingFormik.values.paymentOption === 'pay_later' ? (
                  <div className="text-left mb-6">
                    <p className="text-gray-600 mb-4">
                      Thank you for booking with Teem Wildlife Wonders. Your reservation has been confirmed and will be held for 7 days.
                    </p>
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <ExclamationCircleIcon className="h-5 w-5 text-yellow-400" />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-yellow-800">Payment Required</h3>
                          <div className="mt-2 text-sm text-yellow-700">
                            <p>
                              Please complete your payment within 7 days (by {new Date(new Date().setDate(new Date().getDate() + 7)).toLocaleDateString()}) to secure your booking.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <h3 className="text-md font-semibold mb-2">Payment Options:</h3>
                    <ul className="list-disc pl-5 mb-4 text-gray-600">
                      <li><span className="font-medium">MPESA:</span> Send payment to our business number 123456</li>
                      <li><span className="font-medium">Card Payment:</span> Call us at +123-456-7890 to make a card payment over the phone</li>
                      <li><span className="font-medium">Cash:</span> Visit our office at [Office Address] to pay in person</li>
                    </ul>
                    <p className="text-gray-600 text-sm">
                      Please include your booking reference number when making payment. A confirmation email with these details has been sent to your email address.
                    </p>
                  </div>
                ) : (
                  <p className="text-gray-600 mb-6">
                    Thank you for booking with Teem Wildlife Wonders. Your booking has been confirmed and a confirmation email has been sent to your email address.
                  </p>
                )}
                <div className="bg-gray-50 rounded-lg p-6 mb-6 text-left">
                  <h3 className="text-lg font-semibold mb-4">Booking Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Booking Reference</p>
                      <p className="font-medium">{bookingData?.id || 'KT-12345'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Tour</p>
                      <p className="font-medium">{tourData.title}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Date</p>
                      <p className="font-medium">{bookingData?.bookingDate || bookingFormik.values.date}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Travelers</p>
                      <p className="font-medium">{bookingData?.numTravelers || bookingFormik.values.travelers}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Total Amount</p>
                      <p className="font-medium">${bookingData?.totalPrice || calculateTotal()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Payment Status</p>
                      {bookingFormik.values.paymentOption === 'pay_later' ? (
                        <p className="font-medium text-yellow-600">Pending Payment</p>
                      ) : (
                        <p className="font-medium text-green-600">Paid</p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <Link
                    to="/dashboard/bookings"
                    className="bg-primary hover:bg-primary-dark text-white py-2 px-6 rounded-md transition-colors duration-300"
                  >
                    View My Bookings
                  </Link>
                  <Link
                    to="/"
                    className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 py-2 px-6 rounded-md transition-colors duration-300"
                  >
                    Return to Home
                  </Link>
                </div>
              </div>
            )}
          </div>
          
          {/* Sidebar - Booking Summary */}
          <div className="lg:w-1/3">
            <div className="sticky top-24">
              <BookingSummary 
                tour={tourData} 
                travelers={bookingFormik.values.travelers}
                date={bookingFormik.values.date}
                totalPrice={calculateTotal()}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
